// AHA-47349-presenter-can-create-new-slide-type
// Build the iframe src for a plugin / marketplace / developer slide type from its
// resolved base URL + the slide id.
//   • Marketplace / built-in plugins are served from a ROUTE base (…/duck-race/canvas):
//     the SPA reads the id from the last path segment, so the id is appended as
//     `/<id>` — the legacy form, used for EVERY non-developer plugin, unchanged.
//   • Developer slide types (isDeveloperSlideType) are served from a STATIC entry
//     file (…/presenter.html): the plugin reads the id from zoid xprops and its
//     static host 404s on an extra path segment, so the id goes in the query string
//     (`presenter.html?slideId=<id>`). This is opt-in via `isDeveloper`, so a
//     non-developer plugin's URL shape can never flip it.
// Any query string already on the base URL is preserved, and extra params (e.g.
// pluginModalPath) are merged safely for both shapes.

// True when the URL points to a static entry file (…/presenter.html), false for a
// route base (…/canvas). Parses the pathname so an origin-root base with no path
// (`https://my-plugin.example.io`) is NOT misread as a file by its hostname's dot.
export const isStaticFileUrl = (url) => {
  let path = String(url || '').split(/[?#]/)[0]
  try {
    path = new URL(path).pathname
  } catch (e) {
    /* relative URL (no origin): use the string as-is */
  }
  return /\.[a-z0-9]+$/i.test(path.slice(path.lastIndexOf('/') + 1))
}

const buildQuery = (params) => Object.keys(params)
  .filter((key) => params[key] != null && params[key] !== '')
  .map((key) => `${key}=${encodeURIComponent(params[key])}`)
  .join('&')

export const buildPluginIframeUrl = (baseUrl, slideId, { params = {}, isDeveloper = false } = {}) => {
  if (!baseUrl) return baseUrl
  const str = String(baseUrl)
  const qIdx = str.indexOf('?')
  const base = qIdx === -1 ? str : str.slice(0, qIdx)
  const existingQuery = qIdx === -1 ? '' : str.slice(qIdx + 1)
  const isStatic = isDeveloper && isStaticFileUrl(base)
  // Static developer entry: the id rides the query string. Route base: the id is a
  // trailing path segment, inserted BEFORE any pre-existing query.
  const pathPart = isStatic ? base : `${base}/${slideId}`
  const query = buildQuery(isStatic ? { slideId, ...params } : params)
  const merged = [existingQuery, query].filter(Boolean).join('&')
  return merged ? `${pathPart}?${merged}` : pathPart
}

export default buildPluginIframeUrl
